import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useColor } from '@hub/hooks';
import { IUnitTestProps } from '@monorepo/client-types';
import {
  Card,
  Col,
  Flex,
  Row,
  Statistic,
  TextComponent,
  TextComponentProps,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { ThemeColors } from '@monorepo/types';
import DashboardTooltip from '../dashboard-tooltip/dashboard-tooltip';

import './advanced-statistics.scss';

export type AdvancedStatisticsType = {
  title: string;
  tooltipText?: ReactNode;
  value?: number | string;
  valueColor?: ThemeColors;
  valuePrefix?: string;
  valueSuffix?: string;
  valuePrecision?: number;
  valueWeight?: TextComponentProps['weight'];
};

export interface AdvancedStatisticsCardProps extends IUnitTestProps {
  leftColumn?: AdvancedStatisticsType[];
  rightColumn?: AdvancedStatisticsType[];
  isLoading?: boolean;
  renderView?: 'row' | 'cards';
}

const AdvancedStatisticsCard: FunctionComponent<
  AdvancedStatisticsCardProps
> = ({
  leftColumn = [],
  rightColumn = [],
  isLoading,
  renderView = 'row',
  testId,
}) => {
  const { isDesktop } = useResponsiveness();
  const { t } = useTranslation(['common']);

  return (
    <Flex
      vertical
      className="advanced-statistics"
      gap={isDesktop ? 25 : 18}
      data-testid={testId}
    >
      <TextComponent weight={600} responsive={false} level={5}>
        {t('common:advanced_statistics', {
          defaultValue: 'Advanced Statistics',
        })}
      </TextComponent>
      {renderView === 'row' ? (
        <RowRender
          leftColumn={leftColumn}
          rightColumn={rightColumn}
          isLoading={isLoading}
        />
      ) : (
        <CardsRender
          stats={[...leftColumn, ...rightColumn]}
          isLoading={isLoading}
        />
      )}
    </Flex>
  );
};

const RowRender: FunctionComponent<AdvancedStatisticsCardProps> = ({
  leftColumn = [],
  rightColumn = [],
  isLoading,
}) => {
  const { getColor } = useColor();

  return (
    <Row gutter={[14, 16]}>
      {[leftColumn, rightColumn].map((column, colIndex) => (
        <Col key={colIndex} xs={24} xxl={12}>
          <Flex vertical gap={20}>
            {column.map(
              (
                {
                  title,
                  value,
                  tooltipText,
                  valueColor,
                  valueSuffix,
                  valuePrefix,
                  valuePrecision,
                  valueWeight = 600,
                },
                statIndex,
              ) => (
                <Flex
                  key={statIndex}
                  align="center"
                  gap={5}
                  wrap="nowrap"
                  justify="space-between"
                >
                  <Flex
                    align="center"
                    gap={4}
                    className="advanced-statistics__title-container"
                  >
                    <TextComponent
                      noWrap
                      responsive={false}
                      level={8}
                      className="advanced-statistics__title"
                      ellipsis
                    >
                      {title}
                    </TextComponent>
                    {tooltipText && (
                      <DashboardTooltip
                        absolute={false}
                        tooltipText={tooltipText}
                        tooltipLevel={9}
                      />
                    )}
                  </Flex>
                  <TextComponent
                    noWrap
                    color={valueColor ? getColor(valueColor) : undefined}
                    weight={valueWeight}
                    loading={isLoading}
                    textAlign="left"
                    responsive={false}
                    level={8}
                  >
                    <Statistic
                      prefix={valuePrefix}
                      suffix={valueSuffix}
                      value={value}
                      precision={valuePrecision || 0}
                    />
                  </TextComponent>
                </Flex>
              ),
            )}
          </Flex>
        </Col>
      ))}
    </Row>
  );
};

const CardsRender: FunctionComponent<{
  stats: AdvancedStatisticsType[];
  isLoading?: boolean;
}> = ({ stats, isLoading }) => {
  return (
    <Row gutter={[24, 24]}>
      {stats.map((stat, statIndex) => (
        <Col key={statIndex} xs={24} md={12} xl={8} xxl={6}>
          <Card
            shadowSize="small"
            className="advanced-statistics__card-view"
            hoverable={false}
          >
            <Flex
              justify="space-between"
              align="start"
              style={{ width: '100%' }}
            >
              <Flex gap={12} align="center" style={{ height: '100%' }}>
                <div className="advanced-statistics__card-view__mark" />
                <Flex vertical gap={5}>
                  <TextComponent noWrap responsive={false} level={7}>
                    {stat.title}
                  </TextComponent>
                  <TextComponent
                    noWrap
                    weight={600}
                    loading={isLoading}
                    textAlign="left"
                    responsive={false}
                    level={6}
                  >
                    <Statistic
                      prefix={stat.valuePrefix}
                      suffix={stat.valueSuffix}
                      value={stat.value}
                      precision={stat.valuePrecision || 0}
                    />
                  </TextComponent>
                </Flex>
              </Flex>
              {stat.tooltipText && (
                <DashboardTooltip
                  absolute={false}
                  tooltipText={stat.tooltipText}
                  tooltipLevel={9}
                />
              )}
            </Flex>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default AdvancedStatisticsCard;
